import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { MdModeEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { ArrowUpDown, MoveDown, MoveUp } from "lucide-react";
import { FiSearch } from "react-icons/fi";
import useDebounce, {
  formatDate,
  formatDateTime,
  truncateString,
  useDecodedTokenResult,
} from "../../lib/utils";
import {
  useGetOrdersMutation,
  useGetOrdersWithVendorIdMutation,
  useGetVendorBySearchQuery,
  useSendOtpMutation,
} from "../../api/api";
import { useDispatch } from "react-redux";
import { setAppState } from "../../redux/slices/appState";
import { Input } from "../../@/components/ui/input";

import {
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
} from "../../@/components/ui/table";
import SkeletonTableLoading from "../../skeletonLoading/SkeletonTableLoading";
import Pagination from "../../components/Pagination";
import { Checkbox } from "../../@/components/ui/checkbox";
import { OTP_TIME_MILLISEC, STATUS } from "../../lib/constants";
import { useToast } from "../../@/components/ui/use-toast";
import { Combobox } from "../../@/components/ui/combobox";
import PayoutInvoiceDialog from "./PayoutInvoiceDialog";
import OtpDialog from "./OtpDialog";
import GenericDropdown from "../../components/GenericDropDown";
import SingleDatePicker from "../../components/SingleDatePicker";

interface Data {
  id: number;
  vendorId: number;
  statusId: number;
  totalAmount: number;
  vendorName: string;
  deliveryCharges: number | null;
  paymentProccessingFee: number | null;
  serviceFee: number | null;
  oldDeliveryCharges: number | null;
  oldTotalAmount: number | null;
  couponVendorId: number | null;
}

const OrdersListView: React.FC = () => {
  const [selected, setSelected] = useState<any>({
    name: "All Orders",
    id: undefined,
  });
  const [selectedVendor, setSelectedVendor] = useState<any>();
  const [selectedRows, setSelectedRows] = useState<any>();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchQuery = useDebounce(searchTerm, 500);

  // Fetch vendors based on the debounced search term
  const { data: vendorsData, isLoading } = useGetVendorBySearchQuery(
    { search: debouncedSearchQuery },
    { skip: debouncedSearchQuery.trim() === "" }
  );
  const { data: allVendors, isLoading: vendorsLoading } =
    useGetVendorBySearchQuery({ search: "" });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOTPVerified, setIsOTPVerified] = useState(false);
  const [isOTPDialogOpen, setIsOTPDialogOpen] = useState(false);

  const options = [
    { name: "All Orders", id: undefined },
    { name: "paid", id: 9 },
    { name: "pending", id: 4 },
  ];

  const [data, setData] = useState<Data[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, _] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [rowSelection, setRowSelection] = useState({});
  const { toast } = useToast();
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const [getOrders, { error, isLoading: ordersLoading }] =
    useGetOrdersWithVendorIdMutation();
  const [onSendOtp, { isLoading: isResendingOtp }] = useSendOtpMutation();
  const [timeLeft, setTimeLeft] = useState<number>(OTP_TIME_MILLISEC);
  const { email } = useDecodedTokenResult();
  const [sortingOrder, setSortingOrder] = useState<"asc" | "desc" | undefined>(
    "desc"
  );
  const [sortingColumn, setSortingColumn] = useState<string | undefined>("id");

  const handleSorting = (column: string) => {
    if (sortingColumn === column) {
      setSortingOrder(sortingOrder === "asc" ? "desc" : "asc");
    } else {
      setSortingColumn(column);
      setSortingOrder("asc");
    }
    setPageNumber(1);
  };

  const fetchData = useCallback(async () => {
    try {
      setRowSelection({});
      const response: any = await getOrders({
        getOrdersWithVendorIdDto: {
          vendorId: selectedVendor?.id ? selectedVendor.id : undefined,
          pageNumber,
          pageSize,
          getTotalCount: true,
          tag: selected?.id ? selected.name : undefined,
          sortingOrder,
          sortingColumn,
          startDate: startDate
            ? formatDateTime(startDate, "YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
          endDate: endDate
            ? formatDateTime(
                endDate.setHours(23, 59, 59, 999),
                "YYYY-MM-DDTHH:mm:ssZ"
              )
            : undefined,
        },
      }).unwrap();
      setData(response?.data?.orders || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      console.error("Failed to get products: ", err);
    }
  }, [
    getOrders,
    selected,
    pageNumber,
    sortingColumn,
    sortingOrder,
    startDate,
    endDate,
    selectedVendor?.id,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, pageNumber]);

  const handlePageChange = (newPageNumber: number) => {
    if (newPageNumber < 0 || newPageNumber > Math.ceil(totalCount / pageSize)) {
      return;
    }
    setPageNumber(newPageNumber);
  };

  const handleGetSelectedRows = async () => {
    if (!selectedVendor?.id) {
      toast({
        title: "Error",
        description: "Please select a vendor",
        variant: "default",
      });
      return;
    }
    if (!Object.keys(rowSelection).length) {
      toast({
        title: "Error",
        description: "Please select at least one order",
        variant: "default",
      });
      return;
    }
    const selectedRows = table
      .getSelectedRowModel()
      .rows.map((row) => row.original);
    setSelectedRows(selectedRows);

    if (!isOTPVerified) {
      try {
        await onSendOtp({
          body: { emailOrPhone: email, role: "ADMIN", isSimpleOTP: false },
        }).unwrap();
        setTimeLeft(OTP_TIME_MILLISEC);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to send OTP",
        });
        console.error("Failed to send OTP:", error);
        return;
      }
      setIsOTPDialogOpen(true);
      return;
    }

    setIsDialogOpen(true);
  };

  const columns = useMemo<ColumnDef<Data>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => {
          const rows = table.getRowModel().rows; // Get the rows

          const isAllDeliveredSelected =
            rows.length > 0 &&
            rows.every(
              (row) =>
                row.original.statusId === STATUS.OrderDelivered &&
                row.getIsSelected()
            );

          const isSomeDeliveredSelected =
            rows.length > 0 &&
            rows.some(
              (row) =>
                row.original.statusId === STATUS.OrderDelivered &&
                row.getIsSelected()
            );

          return (
            <div className="flex items-center justify-center">
              <Checkbox
                className="border-white"
                checked={isAllDeliveredSelected}
                onCheckedChange={(value) => {
                  table.getRowModel().rows.forEach((row) => {
                    if (row.original.statusId === STATUS.OrderDelivered) {
                      row.toggleSelected(!!value);
                    }
                  });
                }}
                aria-label="Select all"
              />
            </div>
          );
        },
        meta: {
          isTooltip: false,
        },
        cell: ({ row }) => {
          const canSelect = row.original.statusId === STATUS.OrderDelivered;
          const totalAmount = row.original.totalAmount;

          return (
            <div className="flex items-center justify-center">
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => {
                  if (value && totalAmount < 0) {
                    toast({
                      title: "Error",
                      description:
                        "You can't payout on this order because vendor is in debt",
                      variant: "destructive",
                    });
                    return;
                  }
                  row.toggleSelected(!!value);
                }}
                disabled={!canSelect}
                className={`${
                  !canSelect ? "cursor-not-allowed border-gray-300" : ""
                }`}
                aria-label="Select row"
              />
            </div>
          );
        },
        size: 40,
      },
      {
        accessorKey: "id",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("id")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Order Number{" "}
            {sortingColumn === "id" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
      },
      {
        accessorKey: "vendorName",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("vendorName")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Vendor{" "}
            {sortingColumn === "vendorName" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
      },
      {
        accessorKey: "deliveryCharges",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("deliveryCharges")}
            className="cursor-pointer flex gap-1 items-center text-wrap"
          >
            Delivery Charges{" "}
            {sortingColumn === "deliveryCharges" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 170,
        cell: ({ cell }) => {
          const value =
            cell.getValue() !== null
              ? parseFloat(cell.getValue() as string)
              : 0;
          return <span>${value.toFixed(2)}</span>;
        },
      },
      {
        accessorKey: "paymentProcessingFee",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("paymentProcessingFee")}
            className="cursor-pointer flex gap-1 items-center text-wrap"
          >
            Payment Processing Fee{" "}
            {sortingColumn === "paymentProcessingFee" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 200,
        cell: ({ cell }) => {
          const value =
            cell.getValue() != null ? parseFloat(cell.getValue() as string) : 0;
          return <span>${value.toFixed(2)}</span>;
        },
      },
      {
        accessorKey: "serviceFee",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("serviceFee")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Service Fee{" "}
            {sortingColumn === "serviceFee" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
        cell: ({ cell }) => {
          const value =
            cell.getValue() != null ? parseFloat(cell.getValue() as string) : 0;
          return <span>${value.toFixed(2)}</span>;
        },
      },
      {
        accessorKey: "totalAmount",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("totalAmount")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Total Amount{" "}
            {sortingColumn === "totalAmount" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
        cell: ({ cell }) => {
          const value =
            cell.getValue() != null ? parseFloat(cell.getValue() as string) : 0;
          return <span>${value.toFixed(2)}</span>;
        },
      },
    ],
    [sortingColumn, sortingOrder]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: Math.ceil(totalCount / pageSize),
    manualPagination: true,
    onRowSelectionChange: setRowSelection,
    state: {
      pagination: {
        pageIndex: pageNumber - 1,
        pageSize,
      },
      rowSelection,
    },
  });

  return (
    <div className="overflow-hidden h-full pl-4 pt-2">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2 w-full flex-col sm:flex-row">
        <div className="flex justify-between bg-white rounded-lg border p-[1px] border-gray-300 w-full sm:w-auto">
          {options.map((option) => (
            <button
              key={option.name}
              onClick={() => {
                setPageNumber(1);
                setSelected(option);
                setRowSelection({});
              }}
              className={`flex-1 w-[4.5rem] capitalize p-2 text-center text-[10px] ${
                selected.name === option.name
                  ? "bg-customGreenLight text-customGreenColor font-base"
                  : "text-gray-500 hover:bg-gray-100"
              } rounded-lg ${
                option.name === "All Product" ? "rounded-l-lg" : ""
              } ${option.name === "Draft" ? "rounded-r-lg" : ""}`}
            >
              {option.name}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2 w-full sm:w-auto flex-col sm:flex-row">
          <div className="w-full sm:w-auto">
            <PayoutInvoiceDialog
              selectedVendor={selectedVendor}
              selectedRows={selectedRows}
              handleGetSelectedRows={handleGetSelectedRows}
              isDialogOpen={isDialogOpen}
              setIsDialogOpen={setIsDialogOpen}
              fetchOrderData={fetchData}
            />
            <OtpDialog
              setTimeLeft={setTimeLeft}
              timeLeft={timeLeft}
              isOTPDialogOpen={isOTPDialogOpen}
              setIsOTPDialogOpen={setIsOTPDialogOpen}
              setIsOtpVerified={setIsOTPVerified}
              setOpenPayoutDialog={setIsDialogOpen}
            />
          </div>

          <div className="flex gap-2 flex-col w-full sm:flex-row sm:w-auto">
            <div className="w-full sm:w-auto">
              <GenericDropdown
                labelValue={selectedVendor?.company || ""}
                onChange={(value) => {
                  const vendor = (
                    vendorsData?.data ||
                    allVendors?.data ||
                    []
                  ).find((v) => v.id.toString() === value);
                  setSelectedVendor(vendor || null);
                }}
                options={[
                  ...(vendorsData?.data || allVendors?.data || []).map(
                    (vendor) => ({
                      value: vendor.id.toString(),
                      label: vendor.company,
                    })
                  ),
                ]}
                placeholder="Select Vendor"
                error={false}
                className="text-black bg-white h-9 justify-center items-center w-full sm:w-auto"
                showAllOption={true}
              />
            </div>
            <div className="w-full sm:w-auto">
              <SingleDatePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateSelect={setStartDate}
                onEndDateSelect={setEndDate}
              />
            </div>
          </div>
        </div>
      </div>
      {ordersLoading ? (
        <div className="overflow-y-auto h-[calc(100vh-12rem)] mt-4">
          <div className="overflow-x-auto bg-white rounded-lg shadow h-full">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="bg-customGreenColor text-white"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        style={{
                          minWidth: header.column.columnDef.size,
                          maxWidth: header.column.columnDef.size,
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
            </Table>
            <SkeletonTableLoading />
          </div>
        </div>
      ) : (
        <div className="overflow-y-auto h-[calc(100vh-12rem)]">
          <div className="overflow-x-auto bg-white rounded-lg shadow h-full">
            <Table>
              <TableHeader className="sticky top-0">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="bg-customGreenColor text-white overflow-hidden"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        style={{
                          minWidth: header.column.columnDef.size,
                          maxWidth: header.column.columnDef.size,
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="text-center">
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          style={{
                            minWidth: cell.column.columnDef.size,
                            maxWidth: cell.column.columnDef.size,
                          }}
                          className="overflow-hidden text-ellipsis text-nowrap"
                          isTooltip={
                            cell.column.columnDef.meta?.isTooltip ?? true
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      {data.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 pb-4 overflow-hidden">
          <div className="mb-2 text-[#667085]">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {Math.ceil(totalCount / pageSize)}
          </div>
          <Pagination
            totalCount={totalCount}
            pageSize={pageSize}
            currentPage={pageNumber}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default OrdersListView;
